import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import BuilderShell from "../../components/checkout/BuilderShell";
import api from "../../services/api";

export default function CheckoutBuilderPage() {
    const { id } = useParams<{ id: string }>();
    const [checkoutConfig, setCheckoutConfig] = useState(null);
    const [productId, setProductId] = useState<string | undefined>(undefined);
    const [offerId, setOfferId] = useState<string | undefined>(undefined);
    const [checkoutName, setCheckoutName] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadCheckoutConfig = async () => {
            if (!id) {
                setError("ID do checkout não fornecido");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await api.get(`/api/checkout-builder/${id}`);

                // A API retorna { status, data }, então pegamos response.data.data
                const checkoutData = response.data.data || response.data;

                // Verificar se retornou dados válidos
                if (!checkoutData) {
                    setCheckoutConfig(null);
                } else {
                    // Extrair productId, offerId e name do checkout
                    if (checkoutData.productId) {
                        setProductId(checkoutData.productId);
                    }
                    if (checkoutData.offerId) {
                        setOfferId(checkoutData.offerId);
                    }
                    if (checkoutData.name) {
                        setCheckoutName(checkoutData.name);
                    }

                    // Verificar se tem configurações (novo formato ou antigo)
                    if (checkoutData.configurations) {
                        setCheckoutConfig(checkoutData.configurations);
                    } else if (checkoutData.visualConfig) {
                        setCheckoutConfig(checkoutData.visualConfig);
                    } else {
                        setCheckoutConfig(null);
                    }
                }
            } catch (err: any) {
                console.error("Erro ao carregar configuração do checkout:", err);

                // Se for erro 404, criar checkout em branco
                if (err.response?.status === 404) {
                    console.log(`Checkout ${id} não encontrado. Criando novo em branco.`);
                    setCheckoutConfig(null); // null = checkout em branco

                    // IMPORTANTE: Mesmo com erro 404, não temos productId
                    // O usuário não deveria acessar /checkout-builder/:id sem ter um checkout válido
                    setError("Checkout não encontrado. Por favor, crie um novo checkout a partir da página do produto.");
                } else {
                    // Outros erros (rede, servidor, etc.)
                    setError(err.response?.data?.message || err.message || "Erro desconhecido ao carregar checkout");
                }
            } finally {
                setLoading(false);
            }
        };

        loadCheckoutConfig();
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando checkout builder...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-xl mb-4">❌</div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <PageMeta
                title="Personalizar Checkout | Sellyo"
                description="Checkout Builder"
            />
            <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-4">
                <BuilderShell
                    checkoutId={id}
                    productId={productId}
                    offerId={offerId}
                    checkoutName={checkoutName}
                    initialConfig={checkoutConfig}
                />
            </div>
        </div>
    );
}
